import React, { useState } from "react";
import Header from "./Header";
import StepIndicator from "./StepIndicator";
import StepProgressIndicator from "./StepProgressIndicator";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThree from "./StepThree";
import StepFour from "./StepFour";
import "./WizardStyles.css";

const Wizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTokens, setSelectedTokens] = useState([]);
  const totalSteps = 4;

  // New state to accumulate data from each step
  const [raceData, setRaceData] = useState({});
  // State to control review modal visibility
  const [showReview, setShowReview] = useState(false);

  const handleNextStep = (data) => {
    // Merge current step's data into the raceData object if any data is passed
    if (data) {
      setRaceData((prev) => ({ ...prev, ...data }));
      // Special handling for step 1 to capture selected tokens
      if (currentStep === 1 && data.selectedTokens) {
        setSelectedTokens(data.selectedTokens);
      }
    }
    // If it's the last step, instead of advancing, show the review modal
    if (currentStep === totalSteps) {
      setShowReview(true);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Closes the review modal
  const closeReviewModal = () => {
    setShowReview(false);
  };

  // Optionally, you might add a confirm function to finalize the race data
  // Here the submit button is disabled so confirmRace won't be triggered.
  const confirmRace = () => {
    // For example, send raceData to the backend or navigate away
    console.log("Final Race Data:", raceData);
    setShowReview(false);
  };

  const renderStepContent = () => {
    const commonProps = {
      onNext: handleNextStep,
      onBack: handlePreviousStep,
      currentStep: currentStep,
      totalSteps: totalSteps,
      isLastStep: currentStep === totalSteps,
    };

    switch (currentStep) {
      case 1:
        return (
          <StepOne {...commonProps} initialSelectedTokens={selectedTokens} />
        );
      case 2:
        // Pass the existing raceData as initialRaceSettings so StepTwo can preload its fields.
        return <StepTwo {...commonProps} initialRaceSettings={raceData} />;
      case 3:
        // Pass selectedTokens and any saved handicap settings so StepThree can preload its slider values.
        return (
          <StepThree
            {...commonProps}
            selectedTokens={selectedTokens}
            initialHandicapSettings={raceData.handicapSettings}
          />
        );
      case 4:
        return <StepFour {...commonProps} selectedTokens={selectedTokens} />;
      default:
        return null;
    }
  };

  // Create a nicely formatted summary from the accumulated raceData and selectedTokens.
  const renderReviewSummary = () => {
    const {
      raceName,
      raceDuration,
      startDate,
      startTime,
      bettingWindow,
      handicapSettings,
      usdcAmount,
      allocations,
      creatorCommission,
      houseCommission,
    } = raceData;

    const formattedStart =
      startDate && startTime
        ? new Date(startDate + "T" + startTime).toLocaleString()
        : "Not set";

    return (
      <div className="review-summary">
        <h3>Selected Tokens</h3>
        <ul>
          {selectedTokens.map((token) => (
            <li key={token.id}>
              {token.name} ({token.symbol})
            </li>
          ))}
        </ul>

        <h3>Race Settings</h3>
        <p>
          <strong>Race Name:</strong> {raceName || "Not set"}
        </p>
        <p>
          <strong>Duration:</strong>{" "}
          {raceDuration ? `${raceDuration} hrs` : "Not set"}
        </p>
        <p>
          <strong>Start Time:</strong> {formattedStart}
        </p>
        <p>
          <strong>Betting Window:</strong>{" "}
          {bettingWindow
            ? `Before: ${bettingWindow.before} mins, After: ${bettingWindow.after} mins`
            : "Not set"}
        </p>

        <h3>Handicap Settings</h3>
        <p>
          <strong>Market Cap Weight:</strong>{" "}
          {handicapSettings ? handicapSettings.marketCapWeight : "50"}
        </p>
        <p>
          <strong>Volatility Weight:</strong>{" "}
          {handicapSettings ? handicapSettings.volatilityWeight : "50"}
        </p>

        <h3>Pool & Commission</h3>
        <p>
          <strong>USDC Amount:</strong>{" "}
          {usdcAmount ? `$${usdcAmount}` : "Not set"}
        </p>
        <p>
          <strong>Allocations:</strong>{" "}
          {allocations ? JSON.stringify(allocations) : "Not set"}
        </p>
        <p>
          <strong>Your Commission:</strong>{" "}
          {creatorCommission !== undefined ? `${creatorCommission}%` : "0%"}
        </p>
        <p>
          <strong>House Commission:</strong>{" "}
          {houseCommission !== undefined ? `${houseCommission}%` : "2.5%"}
        </p>
      </div>
    );
  };

  return (
    <div className="wizard-container">
      <div className="header-wrapper">
        <Header />
      </div>
      <div className="wizard-content">
        <div className="wizard-steps">
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
          <StepProgressIndicator
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
        </div>
        <div className="step-content-box">{renderStepContent()}</div>
      </div>

      {/* Review Modal */}
      {showReview && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Review Race Information</h2>
            <div className="modal-content">{renderReviewSummary()}</div>
            <div className="modal-actions">
              <button onClick={closeReviewModal} className="modal-button">
                Edit
              </button>
              <button
                onClick={confirmRace}
                className="modal-button confirm"
                disabled
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wizard;
